/**
 * Island Rendering System
 * 
 * Handles loading, rendering, and unloading of island chunks.
 * Extracted from OceanScene for modularity.
 */

import Phaser from 'phaser';
import { TILE_SIZE, CHUNK_SIZE, IslandData, FishingSpotData } from '../types';
import { drawTerritoryZone } from '../utils/RadiusRenderer';
import CacheManager from '../../utils/CacheManager';
import RetryManager from '../../utils/RetryManager';
import { TilesetManager } from '../managers/TilesetManager';

// Biome-specific color palettes
const BIOME_COLORS: Record<string, { shore: number; main: number; accent: number; feature: number }> = {
  beach:    { shore: 0xf5deb3, main: 0xc2b280, accent: 0xd4c896, feature: 0x87ceeb },
  forest:   { shore: 0xc2b280, main: 0x228b22, accent: 0x2e8b2e, feature: 0x1a5f1a },
  mountain: { shore: 0x8b8b83, main: 0x696969, accent: 0x808080, feature: 0xffffff },
  jungle:   { shore: 0xc2b280, main: 0x006400, accent: 0x228b22, feature: 0x2f4f2f },
  desert:   { shore: 0xf4a460, main: 0xdaa520, accent: 0xcd853f, feature: 0x8b4513 },
  volcanic: { shore: 0x2f2f2f, main: 0x1a1a1a, accent: 0x3d3d3d, feature: 0xff4500 },
  winter:   { shore: 0xe0e0e0, main: 0xfffafa, accent: 0xf0f8ff, feature: 0x87ceeb },
  mixed:    { shore: 0xc2b280, main: 0x4a7c23, accent: 0x5a8c33, feature: 0x2a4a6a },
};

// Blocked water (cloudy) overlay
const BLOCKED_WATER_COLOR = 0x6ba6ff; // soft blue
const ISLAND_DECOR_CHANCE = 0.09; // 3x more common (was 0.03)
const ISLAND_DECOR_KEYS: Array<string> = [
  'island_decor_01',
  'island_decor_02',
  'island_decor_03',
  'island_decor_04',
  'island_decor_05',
  'island_decor_06',
  'island_decor_07',
  'island_decor_08',
];

export interface IslandRendererCallbacks {
  onFishingSpotsLoaded?: (spots: FishingSpotData[], chunkId: string) => void;
  onIslandRightClick?: (island: IslandData, screenX: number, screenY: number) => void;
  onPermanentLocationLoaded?: (island: IslandData) => void; // Callback for permanent locations
  onChunkUnloaded?: (chunkId: string) => void; // Callback when a chunk is unloaded
  onPlacedTilesLoaded?: (tiles: any[], chunkId: string) => void; // Callback for placed tiles from tile editor
  getLocalPlayerId?: () => string;
  getCurrentRiftId?: () => string; // Get current rift for chunk generation
}

interface IslandEntry {
  graphics: Phaser.GameObjects.Graphics | Phaser.GameObjects.Container;
  x: number;
  y: number;
  islandData: IslandData;
  hitArea?: Phaser.GameObjects.Rectangle | Phaser.GameObjects.Zone;
  label?: Phaser.GameObjects.Text;
  typeLabel?: Phaser.GameObjects.Text;
  territoryLabel?: Phaser.GameObjects.Text;
  sprites?: Phaser.GameObjects.Sprite[];
}

export class IslandRenderer {
  private scene: Phaser.Scene;
  private islands: Map<string, IslandEntry> = new Map();
  private loadedChunks: Set<string> = new Set();
  private callbacks: IslandRendererCallbacks;
  private tilesetManager: TilesetManager | null = null;
  private interiorTiles: Set<string> = new Set(); // Track interior (non-shore) island tiles

  constructor(scene: Phaser.Scene, callbacks: IslandRendererCallbacks) {
    this.scene = scene;
    this.callbacks = callbacks;
  }

  setTilesetManager(manager: TilesetManager): void {
    this.tilesetManager = manager;
  }

  getIslands(): Map<string, IslandEntry> {
    return this.islands;
  }

  getLoadedChunks(): Set<string> {
    return this.loadedChunks;
  }

  /**
   * Check if a tile is an interior island tile (not a shore tile)
   * Ocean should NOT render under interior tiles, but SHOULD render under shore tiles
   */
  public isInteriorIslandTile(tileX: number, tileY: number): boolean {
    return this.interiorTiles.has(`${tileX},${tileY}`);
  }


  /**
   * Clear all islands and reset chunk tracking (used on world reset)
   */
  clearIslands(): void {
    this.islands.forEach((entry) => {
      entry.graphics.destroy();
      entry.hitArea?.destroy();
      entry.label?.destroy();
      entry.typeLabel?.destroy();
      entry.territoryLabel?.destroy();
      entry.sprites?.forEach(sprite => sprite.destroy());
    });
    this.islands.clear();
    this.interiorTiles.clear();
    this.loadedChunks.clear();
    
    // Clear enhanced cache
    CacheManager.clearChunks();
    
    console.log('🗑️ Cleared all islands and cache from renderer');
  }

  /**
   * Load nearby island chunks based on player position
   */
  loadNearbyIslands(playerX: number, playerY: number): void {
    const chunkX = Math.floor(playerX / CHUNK_SIZE);
    const chunkY = Math.floor(playerY / CHUNK_SIZE);
    
    const activeChunks = new Set<string>();
    
    // Load 3x3 grid around player
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        const cx = chunkX + dx;
        const cy = chunkY + dy;
        if (cx >= 0 && cy >= 0 && cx < 8 && cy < 8) {
          const chunkId = `${cx}_${cy}`;
          activeChunks.add(chunkId);
          this.loadChunk(cx, cy);
        }
      }
    }
    
    // Unload distant chunks
    const chunksToUnload: string[] = [];
    Array.from(this.loadedChunks).forEach(loadedChunkId => {
      if (!activeChunks.has(loadedChunkId)) {
        const [cx, cy] = loadedChunkId.split('_').map(Number);
        const distX = Math.abs(cx - chunkX);
        const distY = Math.abs(cy - chunkY);
        if (distX > 2 || distY > 2) {
          chunksToUnload.push(loadedChunkId);
        }
      }
    });
    
    for (const chunkId of chunksToUnload) {
      this.unloadChunk(chunkId);
    }
  }

  /**
   * Load island chunks based on the camera's visible world bounds.
   * This prevents "invisible islands" when the camera can see farther than the player-centric 3x3.
   * Padding is calculated dynamically based on viewport size to handle variable camera dimensions.
   */
  loadVisibleIslands(
    worldView: Phaser.Geom.Rectangle,
    options?: {
      paddingPx?: number;
    }
  ): void {
    // Calculate padding dynamically: ensure we load at least 1 chunk beyond visible area
    // This accounts for variable viewport sizes (640px to 1200px)
    const dynamicPadding: number = Math.max(CHUNK_SIZE, worldView.width * 0.25, worldView.height * 0.25);
    const paddingPx: number = options?.paddingPx ?? dynamicPadding;

    const minX: number = Math.max(0, worldView.x - paddingPx);
    const minY: number = Math.max(0, worldView.y - paddingPx);
    const maxX: number = Math.max(0, worldView.right + paddingPx);
    const maxY: number = Math.max(0, worldView.bottom + paddingPx);

    const minChunkX: number = Math.max(0, Math.floor(minX / CHUNK_SIZE));
    const minChunkY: number = Math.max(0, Math.floor(minY / CHUNK_SIZE));
    const maxChunkX: number = Math.min(7, Math.ceil(maxX / CHUNK_SIZE) - 1);
    const maxChunkY: number = Math.min(7, Math.ceil(maxY / CHUNK_SIZE) - 1);

    const activeChunks: Set<string> = new Set();
    for (let cx = minChunkX; cx <= maxChunkX; cx++) {
      for (let cy = minChunkY; cy <= maxChunkY; cy++) {
        const chunkId: string = `${cx}_${cy}`;
        activeChunks.add(chunkId);
        this.loadChunk(cx, cy);
      }
    }

    const chunksToUnload: string[] = [];
    Array.from(this.loadedChunks).forEach((loadedChunkId: string) => {
      if (!activeChunks.has(loadedChunkId)) {
        chunksToUnload.push(loadedChunkId);
      }
    });

    for (const chunkId of chunksToUnload) {
      this.unloadChunk(chunkId);
    }
  }

  private unloadChunk(chunkId: string): void {
    const [cx, cy] = chunkId.split('_').map(Number);
    const chunkMinX = cx * CHUNK_SIZE;
    const chunkMaxX = (cx + 1) * CHUNK_SIZE;
    const chunkMinY = cy * CHUNK_SIZE;
    const chunkMaxY = (cy + 1) * CHUNK_SIZE;
    
    const islandsToRemove: string[] = [];
    this.islands.forEach((entry, islandId) => {
      // Use stored position instead of getBounds()
      if (entry.x >= chunkMinX && entry.x < chunkMaxX && entry.y >= chunkMinY && entry.y < chunkMaxY) {
        islandsToRemove.push(islandId);
      }
    });
    
    for (const islandId of islandsToRemove) {
      const entry = this.islands.get(islandId);
      if (entry) {
        entry.graphics.destroy();
        entry.hitArea?.destroy();
        entry.label?.destroy();
        entry.typeLabel?.destroy();
        entry.territoryLabel?.destroy();
        this.islands.delete(islandId);
      }
    }
    
    this.loadedChunks.delete(chunkId);
    console.log(`🗑️ Unloaded chunk ${chunkId}: removed ${islandsToRemove.length} islands`);
    
    // Notify callback so PermanentLocationRenderer can also unload
    if (this.callbacks.onChunkUnloaded) {
      this.callbacks.onChunkUnloaded(chunkId);
    }
  }

  private async loadChunk(chunkX: number, chunkY: number): Promise<void> {
    const chunkId = `${chunkX}_${chunkY}`;
    if (this.loadedChunks.has(chunkId)) return;
    
    this.loadedChunks.add(chunkId);
    
    // Get current rift context from callbacks
    const riftId = this.callbacks.getCurrentRiftId?.() || 'core';
    
    // Make cache key rift-aware to prevent loading core world chunks in rifts
    const cacheKey = riftId === 'core' ? chunkId : `${riftId}:${chunkId}`;
    
    const cachedData = CacheManager.getChunk(cacheKey);
    if (cachedData) {
      console.log(`🏝️ Loading chunk (${chunkX}, ${chunkY}) from cache [rift: ${riftId}]`);
      if (cachedData.islands) {
        cachedData.islands.forEach((island: IslandData) => {
          this.createIslandSprite(island);
        });
      }
      if (cachedData.fishingSpots && this.callbacks.onFishingSpotsLoaded) {
        this.callbacks.onFishingSpotsLoaded(cachedData.fishingSpots, chunkId);
      }
      this.scene.events.emit('world:chunkLoaded', { chunkId, chunkX, chunkY });
      return;
    }
    
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      
      const result = await RetryManager.execute(async () => {
        const response = await fetch(`${apiUrl}/api/world/chunk/${chunkX}/${chunkY}?riftId=${riftId}`);
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        return response.json();
      }, RetryManager.createCacheRetryOptions());
      
      if (result.error) {
        throw result.error;
      }
      
      const data = result.data;
      console.log(`🏝️ Loaded chunk (${chunkX}, ${chunkY}) from server [rift: ${riftId}]: ${data.islands?.length || 0} islands, ${data.fishingSpots?.length || 0} fishing spots`);
      
      if (data.islands) {
        data.islands.forEach((island: IslandData) => {
          this.createIslandSprite(island);
        });
      }
      
      // Notify about fishing spots
      if (data.fishingSpots && data.fishingSpots.length > 0 && this.callbacks.onFishingSpotsLoaded) {
        this.callbacks.onFishingSpotsLoaded(data.fishingSpots, chunkId);
      }

      // Notify about placed tiles from tile editor
      if (data.placedTiles && data.placedTiles.length > 0 && this.callbacks.onPlacedTilesLoaded) {
        this.callbacks.onPlacedTilesLoaded(data.placedTiles, chunkId);
      }

      // Notify UI layer that at least one chunk has been loaded
      this.scene.events.emit('world:chunkLoaded', { chunkId, chunkX, chunkY });
      
      // Cache the fetched data using rift-aware cache key
      const serverTimestamp = Date.now();
      CacheManager.setChunk(cacheKey, data, { serverTimestamp });
    } catch (error) {
      console.error(`Failed to load chunk ${chunkId}:`, error);
      this.loadedChunks.delete(chunkId);
    }
  }

  /**
   * Preload chunks in a radius around the player for smoother movement
   */
  async preloadChunksAroundPlayer(playerX: number, playerY: number, radius: number = 2): Promise<void> {
    const chunkX = Math.floor(playerX / CHUNK_SIZE);
    const chunkY = Math.floor(playerY / CHUNK_SIZE);
    
    // Get current rift context
    const riftId = this.callbacks.getCurrentRiftId?.() || 'core';
    
    const chunksToPreload: string[] = [];
    
    // Collect chunks in the specified radius
    for (let dx = -radius; dx <= radius; dx++) {
      for (let dy = -radius; dy <= radius; dy++) {
        const targetChunkX = chunkX + dx;
        const targetChunkY = chunkY + dy;
        
        // Skip if outside world bounds
        if (targetChunkX < 0 || targetChunkX >= 8 || targetChunkY < 0 || targetChunkY >= 8) {
          continue;
        }
        
        const chunkId = `${targetChunkX}_${targetChunkY}`;
        
        // Make cache key rift-aware
        const cacheKey = riftId === 'core' ? chunkId : `${riftId}:${chunkId}`;
        
        // Only preload if not already loaded and not cached
        if (!this.loadedChunks.has(chunkId) && !CacheManager.hasChunk(cacheKey)) {
          chunksToPreload.push(chunkId);
        }
      }
    }
    
    if (chunksToPreload.length > 0) {
      console.log(`🚀 Preloading ${chunksToPreload.length} chunks around player [rift: ${riftId}]`);
      // Transform chunk IDs to be rift-aware before preloading
      const riftAwareChunkIds = chunksToPreload.map(chunkId => 
        riftId === 'core' ? chunkId : `${riftId}:${chunkId}`
      );
      await CacheManager.preloadChunks(riftAwareChunkIds);
    }
  }

  private createIslandSprite(island: IslandData): void {
    if (this.islands.has(island.id)) return;
    
    // Skip permanent locations - they are handled by PermanentLocationRenderer
    // This check prevents regular islands from being rendered if they somehow match patterns
    const id = island.id.toLowerCase();
    const name = island.permanentName?.toLowerCase() || '';
    if (id.includes('trading_post') || id.includes('harbor_office') || 
        id.includes('tavern') || id.includes('shipyard') ||
        name.includes('trading post') || name.includes('harbor office') ||
        name.includes('harbor master') || name.includes('port authority') ||
        name.includes('fleet command') || name.includes('tavern') ||
        name.includes('shipyard')) {
      // Pass to PermanentLocationRenderer via callback
      if (this.callbacks.onPermanentLocationLoaded) {
        this.callbacks.onPermanentLocationLoaded(island);
      }
      return; // Let PermanentLocationRenderer handle it
    }
    
    const biomeKey = island.biome || 'mixed';
    const colors = BIOME_COLORS[biomeKey] || BIOME_COLORS.mixed;
    
    const graphics = this.scene.add.graphics();
    graphics.setDepth(1);
    
    // Draw territory zone for claimed islands (before island itself)
    if (island.ownerId && island.claimRadius) {
      const localPlayerId = this.callbacks.getLocalPlayerId?.() || '';
      const isOwnTerritory = island.ownerId === localPlayerId;
      const territoryRadiusTiles = Math.round(island.claimRadius / TILE_SIZE);
      // Use owner's ship color, fallback to blue for players, red for crews
      let ownerColor = 0x3498db; // Default blue
      if (island.ownerShipColor) {
        ownerColor = parseInt(island.ownerShipColor.replace('#', ''), 16);
      } else if (island.ownerType === 'crew') {
        ownerColor = 0xe74c3c; // Red for crews without color
      }
      drawTerritoryZone(graphics, island.x, island.y, territoryRadiusTiles, ownerColor, isOwnTerritory);
    }
    
    const sprites: Array<Phaser.GameObjects.Sprite> = [];
    if (island.shape && island.shape.length >= 3) {
      this.renderPolygonIsland(graphics, island, colors, sprites);
    } else {
      this.renderRadiusIsland(graphics, island, colors, sprites);
    }
    
    // Add name label for permanent islands
    let label: Phaser.GameObjects.Text | undefined;
    let typeLabel: Phaser.GameObjects.Text | undefined;
    let territoryLabel: Phaser.GameObjects.Text | undefined;
    if (island.isPermanent && island.permanentName) {
      const radiusTiles = Math.max(2, Math.round(island.radius / TILE_SIZE));
      label = this.scene.add.text(island.x, island.y - radiusTiles * TILE_SIZE - 8, island.permanentName, {
        fontFamily: '"Sometype Mono", monospace',
        fontSize: '9px',
        color: '#ffd700',
        backgroundColor: '#000000aa',
        padding: { x: 4, y: 2 },
      });
      label.setOrigin(0.5);
      label.setDepth(2);
      
      // Add secondary type label beneath the name
      typeLabel = this.scene.add.text(island.x, island.y - radiusTiles * TILE_SIZE + 6, this.getIslandTypeLabel(island), {
        fontFamily: '"Sometype Mono", monospace',
        fontSize: '6px',
        color: '#aaaaaa',
        backgroundColor: '#000000aa',
        padding: { x: 2, y: 1 },
      });
      typeLabel.setOrigin(0.5);
      typeLabel.setDepth(2);
    }

    // Territory name label (centered on landmass) for claimed islands
    if (!island.isPermanent && island.ownerId && island.customName && island.customName.trim().length > 0) {
      const wrapWidth: number = Math.max(60, Math.min(160, Math.round(island.radius * 2)));
      let labelColor = '#ffffff'; // Default white for player territories
      if (island.ownerType === 'faction_regime') {
        labelColor = '#e74c3c'; // Red for Regime
      } else if (island.ownerType === 'crew') {
        labelColor = '#f39c12'; // Orange for crews
      }
      territoryLabel = this.scene.add.text(island.x, island.y, island.customName, {
        fontFamily: '"Sometype Mono", monospace',
        fontSize: '9px',
        color: labelColor,
        backgroundColor: '#000000aa',
        padding: { x: 4, y: 2 },
        align: 'center',
        wordWrap: { width: wrapWidth, useAdvancedWrap: true },
      });
      territoryLabel.setOrigin(0.5);
      territoryLabel.setDepth(2);
    }
    
    // Hit area should roughly match the rendered island shape to avoid
    // swallowing nearby objects like resource nodes. For polygon islands,
    // approximate with a polygon hit area; for radius islands, use a
    // grid-aligned rectangle matching the drawn tiles.
    let hitArea: Phaser.GameObjects.Rectangle | Phaser.GameObjects.Zone;

    if (island.shape && island.shape.length >= 3) {
      // Polygon-based island: use the same world polygon as the render logic
      const worldPoly = island.shape.map(p => ({ x: island.x + p.x, y: island.y + p.y }));
      const polyPoints: number[] = [];
      worldPoly.forEach(p => {
        polyPoints.push(p.x, p.y);
      });

      const poly = new Phaser.Geom.Polygon(polyPoints);
      // Use explicit width/height arguments so TypeScript matches the Phaser signature
      const zone = this.scene.add.zone(island.x, island.y, 1, 1);
      // Use "any" to avoid Phaser type overload issues here; at runtime this
      // correctly treats the polygon as the hit area.
      (zone as any).setInteractive(poly, Phaser.Geom.Polygon.Contains);
      zone.setDepth(1);
      hitArea = zone;
    } else {
      // Radius-based island: compute the exact tile-aligned rectangle used in renderRadiusIsland
      const radiusTiles = Math.max(2, Math.round(island.radius / TILE_SIZE));
      const centerTileX = Math.floor(island.x / TILE_SIZE);
      const centerTileY = Math.floor(island.y / TILE_SIZE);
      const minTileX = centerTileX - radiusTiles;
      const maxTileX = centerTileX + radiusTiles;
      const minTileY = centerTileY - radiusTiles;
      const maxTileY = centerTileY + radiusTiles;

      const width = (maxTileX - minTileX + 1) * TILE_SIZE;
      const height = (maxTileY - minTileY + 1) * TILE_SIZE;
      const centerX = (minTileX + maxTileX + 1) * TILE_SIZE / 2;
      const centerY = (minTileY + maxTileY + 1) * TILE_SIZE / 2;

      const rect = this.scene.add.rectangle(centerX, centerY, width, height, 0x000000, 0);
      rect.setInteractive();
      rect.setDepth(1);
      hitArea = rect;
    }

    hitArea.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (pointer.rightButtonDown() && this.callbacks.onIslandRightClick) {
        const camera = this.scene.cameras.main;
        const screenX = pointer.worldX - camera.scrollX;
        const screenY = pointer.worldY - camera.scrollY;
        this.callbacks.onIslandRightClick(island, screenX, screenY);
        if (pointer.event && typeof pointer.event.stopPropagation === 'function') {
          pointer.event.stopPropagation();
        }
      }
    });
    
    this.islands.set(island.id, {
      graphics,
      x: island.x,
      y: island.y,
      islandData: island,
      hitArea,
      label,
      typeLabel,
      territoryLabel,
      sprites: sprites.length > 0 ? sprites : undefined,
    });
  }

  private rebuildIslandSprite(islandId: string): void {
    const entry = this.islands.get(islandId);
    if (!entry) return;

    entry.graphics.destroy();
    entry.hitArea?.destroy();
    entry.label?.destroy();
    entry.typeLabel?.destroy();
    entry.territoryLabel?.destroy();
    entry.sprites?.forEach(sprite => sprite.destroy());
    this.islands.delete(islandId);

    this.createIslandSprite(entry.islandData);
  }

  private renderPolygonIsland(
    graphics: Phaser.GameObjects.Graphics,
    island: IslandData,
    colors: { shore: number; main: number; accent: number; feature: number },
    sprites: Array<Phaser.GameObjects.Sprite>
  ): void {
    const worldPoly = island.shape!.map(p => ({ x: island.x + p.x, y: island.y + p.y }));
    const minX = Math.min(...worldPoly.map(p => p.x));
    const maxX = Math.max(...worldPoly.map(p => p.x));
    const minY = Math.min(...worldPoly.map(p => p.y));
    const maxY = Math.max(...worldPoly.map(p => p.y));
    
    const startTileX = Math.floor(minX / TILE_SIZE);
    const endTileX = Math.ceil(maxX / TILE_SIZE);
    const startTileY = Math.floor(minY / TILE_SIZE);
    const endTileY = Math.ceil(maxY / TILE_SIZE);
    
    const seed = island.x * 1000 + island.y;
    const seededRandom = (offset: number): number => {
      const x = Math.sin(seed + offset) * 10000;
      return x - Math.floor(x);
    };
    
    const insideTiles: Set<string> = new Set();
    const biome = island.biome || 'mixed';
    const tileData: Array<{ tx: number; ty: number; color: number; tileType: string }> = [];

    // First pass: identify all inside tiles and build the complete set
    for (let ty = startTileY; ty <= endTileY; ty++) {
      for (let tx = startTileX; tx <= endTileX; tx++) {
        const centerX = tx * TILE_SIZE + TILE_SIZE / 2;
        const centerY = ty * TILE_SIZE + TILE_SIZE / 2;
        if (this.pointInPolygon(centerX, centerY, worldPoly)) {
          insideTiles.add(`${tx},${ty}`);
          const distFromCenter = Math.sqrt((centerX - island.x) ** 2 + (centerY - island.y) ** 2);
          const maxDist = Math.max(...worldPoly.map(p => Math.sqrt((p.x - island.x) ** 2 + (p.y - island.y) ** 2)));
          const edgeRatio = distFromCenter / maxDist;
          
          let color: number;
          const rand = seededRandom(tx * 30 + ty * 30);
          if (edgeRatio < 0.3 && seededRandom(tx * 50 + ty * 50) > 0.88) {
            color = colors.feature;
          } else if (rand > 0.65) {
            color = colors.main;
          } else if (rand > 0.35) {
            color = colors.accent;
          } else {
            color = colors.feature;
          }
          
          const tileType = this.getTileTypeFromColor(color, colors, biome);
          tileData.push({ tx, ty, color, tileType });
        }
      }
    }

    const smoothedInsideTiles = this.pruneThreeSideTiles(insideTiles);

    // Second pass: render all tiles with complete insideTiles set available
    tileData.forEach(({ tx, ty, color, tileType }) => {
      if (!smoothedInsideTiles.has(`${tx},${ty}`)) {
        return;
      }
      const hasWaterNeighbor =
        !smoothedInsideTiles.has(`${tx},${ty - 1}`) ||
        !smoothedInsideTiles.has(`${tx + 1},${ty - 1}`) ||
        !smoothedInsideTiles.has(`${tx + 1},${ty}`) ||
        !smoothedInsideTiles.has(`${tx + 1},${ty + 1}`) ||
        !smoothedInsideTiles.has(`${tx},${ty + 1}`) ||
        !smoothedInsideTiles.has(`${tx - 1},${ty + 1}`) ||
        !smoothedInsideTiles.has(`${tx - 1},${ty}`) ||
        !smoothedInsideTiles.has(`${tx - 1},${ty - 1}`);

      const finalTileType = hasWaterNeighbor
        ? this.getShorelineTileType(`${biome}_shore`, tx, ty, smoothedInsideTiles)
        : tileType;
      const finalColor = hasWaterNeighbor ? colors.shore : color;

      this.renderTile(graphics, tx * TILE_SIZE, ty * TILE_SIZE, finalColor, finalTileType, insideTiles, sprites);

      // Track interior tiles (non-shore tiles) for ocean rendering optimization
      if (!hasWaterNeighbor) {
        this.interiorTiles.add(`${tx},${ty}`);
      }

      if (!hasWaterNeighbor && this.tilesetManager) {
        const decorSeed = this.getDecorSeed(tx, ty, seed);
        if (decorSeed < ISLAND_DECOR_CHANCE) {
          const decorIndex = Math.floor(decorSeed * 10000) % ISLAND_DECOR_KEYS.length;
          const decorKey = ISLAND_DECOR_KEYS[decorIndex];
          // Island decor keys are direct texture keys (no variants)
          if (this.tilesetManager.hasDirectTexture(decorKey)) {
            const decorSprite = this.scene.add.sprite(
              tx * TILE_SIZE + TILE_SIZE / 2,
              ty * TILE_SIZE + TILE_SIZE / 2,
              decorKey
            );
            decorSprite.setOrigin(0.5, 0.5);
            decorSprite.setDepth(5); // Above island tiles (1) and ocean decor (3-4)
            decorSprite.setScrollFactor(1, 1);
            decorSprite.setScale(0.85);
            sprites.push(decorSprite);
          }
        }
      }
    });

    this.drawBlockedWaterHalo(graphics, smoothedInsideTiles);
  }

  /**
   * Fallback rendering for islands without shape data - uses square grid
   */
  private renderRadiusIsland(
    graphics: Phaser.GameObjects.Graphics,
    island: IslandData,
    colors: { shore: number; main: number; accent: number; feature: number },
    sprites: Array<Phaser.GameObjects.Sprite>
  ): void {
    const radiusTiles = Math.max(2, Math.round(island.radius / TILE_SIZE));
    const centerTileX = Math.floor(island.x / TILE_SIZE);
    const centerTileY = Math.floor(island.y / TILE_SIZE);
    
    const seed = island.x * 1000 + island.y;
    const seededRandom = (offset: number): number => {
      const x = Math.sin(seed + offset) * 10000;
      return x - Math.floor(x);
    };
    
    const insideTiles: Set<string> = new Set();
    const biome = island.biome || 'mixed';
    const tileData: Array<{ tx: number; ty: number; color: number; tileType: string }> = [];

    // First pass: identify all inside tiles and build the complete set
    for (let dy = -radiusTiles; dy <= radiusTiles; dy++) {
      for (let dx = -radiusTiles; dx <= radiusTiles; dx++) {
        const tx = centerTileX + dx;
        const ty = centerTileY + dy;
        
        // Use Chebyshev distance for square shape
        const chebyshevDist = Math.max(Math.abs(dx), Math.abs(dy));
        const edgeDist = radiusTiles - chebyshevDist;
        
        let color: number;
        const rand = seededRandom(dx * 30 + dy * 30);
        if (edgeDist > radiusTiles * 0.5 && seededRandom(dx * 50 + dy * 50) > 0.88) {
          color = colors.feature;
        } else if (rand > 0.65) {
          color = colors.main;
        } else if (rand > 0.35) {
          color = colors.accent;
        } else {
          color = colors.feature;
        }
        
        insideTiles.add(`${tx},${ty}`);
        const tileType = this.getTileTypeFromColor(color, colors, biome);
        tileData.push({ tx, ty, color, tileType });
      }
    }

    const smoothedInsideTiles = this.pruneThreeSideTiles(insideTiles);

    // Second pass: render all tiles with complete insideTiles set available
    tileData.forEach(({ tx, ty, color, tileType }) => {
      if (!smoothedInsideTiles.has(`${tx},${ty}`)) {
        return;
      }
      const hasWaterNeighbor =
        !smoothedInsideTiles.has(`${tx},${ty - 1}`) ||
        !smoothedInsideTiles.has(`${tx + 1},${ty - 1}`) ||
        !smoothedInsideTiles.has(`${tx + 1},${ty}`) ||
        !smoothedInsideTiles.has(`${tx + 1},${ty + 1}`) ||
        !smoothedInsideTiles.has(`${tx},${ty + 1}`) ||
        !smoothedInsideTiles.has(`${tx - 1},${ty + 1}`) ||
        !smoothedInsideTiles.has(`${tx - 1},${ty}`) ||
        !smoothedInsideTiles.has(`${tx - 1},${ty - 1}`);

      const finalTileType = hasWaterNeighbor
        ? this.getShorelineTileType(`${biome}_shore`, tx, ty, smoothedInsideTiles)
        : tileType;
      const finalColor = hasWaterNeighbor ? colors.shore : color;

      this.renderTile(graphics, tx * TILE_SIZE, ty * TILE_SIZE, finalColor, finalTileType, insideTiles, sprites);

      // Track interior tiles (non-shore tiles) for ocean rendering optimization
      if (!hasWaterNeighbor) {
        this.interiorTiles.add(`${tx},${ty}`);
      }
    });

    this.drawBlockedWaterHalo(graphics, smoothedInsideTiles);
  }

  private pruneThreeSideTiles(insideTiles: Set<string>): Set<string> {
    const prunedTiles: Set<string> = new Set(insideTiles);
    const toRemove: Array<string> = [];

    prunedTiles.forEach((key) => {
      const [tx, ty] = key.split(',').map(Number);
      const waterCardinals =
        (!prunedTiles.has(`${tx},${ty - 1}`) ? 1 : 0) +
        (!prunedTiles.has(`${tx + 1},${ty}`) ? 1 : 0) +
        (!prunedTiles.has(`${tx},${ty + 1}`) ? 1 : 0) +
        (!prunedTiles.has(`${tx - 1},${ty}`) ? 1 : 0);

      if (waterCardinals >= 3) {
        toRemove.push(key);
      }
    });

    toRemove.forEach((key) => prunedTiles.delete(key));
    return prunedTiles;
  }

  /**
   * Get a human-readable type label for an island
   * Note: Permanent locations are handled by PermanentLocationRenderer
   */
  private getIslandTypeLabel(island: IslandData): string {
    // Default based on radius (larger = more significant)
    if (island.radius > 80) {
      return 'Major Island';
    }
    if (island.radius > 50) {
      return 'Island';
    }
    return 'Small Island';
  }

  private pointInPolygon(px: number, py: number, poly: Array<{ x: number; y: number }>): boolean {
    let inside = false;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
      const xi = poly[i].x, yi = poly[i].y;
      const xj = poly[j].x, yj = poly[j].y;
      const dyDiff = yj - yi;
      // Skip horizontal edges
      if (Math.abs(dyDiff) < 1e-6) continue;
      const intersect = ((yi > py) !== (yj > py)) && (px < ((xj - xi) * (py - yi)) / dyDiff + xi);
      if (intersect) inside = !inside;
    }
    return inside;
  }

  private getDecorSeed(tileX: number, tileY: number, seed: number): number {
    const hash = Math.sin(tileX * 15731 + tileY * 789221 + seed) * 43758.5453;
    return hash - Math.floor(hash);
  }

  private drawBlockedWaterHalo(graphics: Phaser.GameObjects.Graphics, insideTiles: Set<string>): void {
    if (insideTiles.size === 0) return;

    const haloTiles: Set<string> = new Set();
    insideTiles.forEach((key) => {
      const [tx, ty] = key.split(',').map(Number);
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          if (dx === 0 && dy === 0) continue;
          const nx = tx + dx;
          const ny = ty + dy;
          const neighborKey = `${nx},${ny}`;
          if (insideTiles.has(neighborKey)) continue;
          haloTiles.add(neighborKey);
        }
      }
    });

    graphics.fillStyle(BLOCKED_WATER_COLOR, 0.12);
    haloTiles.forEach((key) => {
      const [tx, ty] = key.split(',').map(Number);
      graphics.fillRect(tx * TILE_SIZE, ty * TILE_SIZE, TILE_SIZE, TILE_SIZE);
    });
  }

  // Permanent location rendering methods moved to PermanentLocationRenderer

   private renderTile(
    graphics: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    color: number,
    tileType: string,
    insideTiles?: Set<string>,
    sprites?: Array<Phaser.GameObjects.Sprite>
  ): string {
    if (this.tilesetManager) {
      const hasTexture = this.tilesetManager.hasTileTexture(tileType);
      if (hasTexture) {
        const texture = this.tilesetManager.getRandomVariant(tileType);
        if (texture) {
          const sprite = this.scene.add.sprite(x, y, texture);
          sprite.setOrigin(0, 0);
          sprite.setDepth(1); // Island base tiles
          
          if (sprites) sprites.push(sprite);
          return 'sprite';
        }
      }

      const fallbackType = tileType.includes('_shore_')
        ? `${tileType.split('_')[0]}_shore`
        : tileType;
      const fallbackTexture = this.tilesetManager.getRandomVariant(fallbackType);
      if (fallbackTexture) {
        const sprite = this.scene.add.sprite(x, y, fallbackTexture);
        sprite.setOrigin(0, 0);
        sprite.setDepth(1); // Island base tiles
        
        if (sprites) sprites.push(sprite);
        return 'sprite';
      }
    } else {
      console.warn('⚠️  TilesetManager not initialized');
    }
    
    graphics.fillStyle(color, 1);
    graphics.fillRect(x, y, TILE_SIZE, TILE_SIZE);
    return 'fallback';
  }

  private getTileTypeFromColor(color: number, colors: { shore: number; main: number; accent: number; feature: number }, biome: string, tileX?: number, tileY?: number, seed?: number): string {
    if (color === colors.shore) return `${biome}_shore`;
    if (color === colors.feature) return `${biome}_feature`;
    if (color === colors.main) return `${biome}_main`;
    if (color === colors.accent) return `${biome}_accent`;
    return `${biome}_main`;
  }

  private getShorelineTileType(baseTileType: string, tileX: number, tileY: number, insideTiles?: Set<string>): string {
    if (!insideTiles || !baseTileType.includes('_shore')) {
      return baseTileType;
    }

    const bitmask = this.calculateShorelineBitmask(tileX, tileY, insideTiles);
    
    if (bitmask === 0) {
      return baseTileType;
    }

    const biome = baseTileType.split('_')[0];
    const shorelineVariant = this.getShorelineVariantFromBitmask(bitmask);
    
    if (shorelineVariant) {
      return `${biome}_shore_${shorelineVariant}`;
    }
    
    return baseTileType;
  }

  private calculateShorelineBitmask(tileX: number, tileY: number, insideTiles: Set<string>): number {
    let bitmask = 0;
    
    const n = insideTiles.has(`${tileX},${tileY - 1}`);
    const ne = insideTiles.has(`${tileX + 1},${tileY - 1}`);
    const e = insideTiles.has(`${tileX + 1},${tileY}`);
    const se = insideTiles.has(`${tileX + 1},${tileY + 1}`);
    const s = insideTiles.has(`${tileX},${tileY + 1}`);
    const sw = insideTiles.has(`${tileX - 1},${tileY + 1}`);
    const w = insideTiles.has(`${tileX - 1},${tileY}`);
    const nw = insideTiles.has(`${tileX - 1},${tileY - 1}`);
    
    if (!n) bitmask |= 1;
    if (!ne) bitmask |= 2;
    if (!e) bitmask |= 4;
    if (!se) bitmask |= 8;
    if (!s) bitmask |= 16;
    if (!sw) bitmask |= 32;
    if (!w) bitmask |= 64;
    if (!nw) bitmask |= 128;
    
    return bitmask;
  }

  private getShorelineVariantFromBitmask(bitmask: number): string | null {
    const hasN = (bitmask & 1) !== 0;
    const hasNE = (bitmask & 2) !== 0;
    const hasE = (bitmask & 4) !== 0;
    const hasSE = (bitmask & 8) !== 0;
    const hasS = (bitmask & 16) !== 0;
    const hasSW = (bitmask & 32) !== 0;
    const hasW = (bitmask & 64) !== 0;
    const hasNW = (bitmask & 128) !== 0;

    const waterCardinals = [hasN, hasE, hasS, hasW].filter(Boolean).length;
    const waterDiagonals = [hasNE, hasSE, hasSW, hasNW].filter(Boolean).length;

    // Inner corners: exactly one diagonal water and both adjacent cardinals are land
    if (waterDiagonals === 1 && hasNE && !hasN && !hasE) return 'inner_sw';
    if (waterDiagonals === 1 && hasSE && !hasS && !hasE) return 'inner_nw';
    if (waterDiagonals === 1 && hasSW && !hasS && !hasW) return 'inner_ne';
    if (waterDiagonals === 1 && hasNW && !hasN && !hasW) return 'inner_se';

    if (waterCardinals === 1) {
      if (hasN) return 'n';
      if (hasE) return 'e';
      if (hasS) return 's';
      if (hasW) return 'w';
    }

    if (waterCardinals === 2) {
      if (hasN && hasE) return 'ne';
      if (hasE && hasS) return 'se';
      if (hasS && hasW) return 'sw';
      if (hasW && hasN) return 'nw';

      if (hasN && hasS) return 'n';
      if (hasE && hasW) return 'e';
    }

    // Three-side water doesn't have a dedicated asset; fall back to base shore.
    if (waterCardinals >= 3) {
      return null;
    }

    // Fallback to diagonal-based corners if cardinals are absent
    if (hasNE) return 'ne';
    if (hasSE) return 'se';
    if (hasSW) return 'sw';
    if (hasNW) return 'nw';

    return null;
  }

  destroy(): void {
    this.islands.forEach(entry => {
      entry.graphics.destroy();
      if (entry.hitArea) {
        entry.hitArea.destroy();
      }
      entry.label?.destroy();
      entry.typeLabel?.destroy();
      entry.territoryLabel?.destroy();
      entry.sprites?.forEach(sprite => sprite.destroy());
    });
    this.islands.clear();
    this.loadedChunks.clear();
  }

  /**
   * Get island data by ID
   */
  getIslandData(islandId: string): IslandData | null {
    const entry = this.islands.get(islandId);
    return entry?.islandData || null;
  }

  /**
   * Update island ownership data (called when territory:update event received)
   */
  updateIslandOwnership(islandId: string, ownership: {
    ownerId: string | null;
    ownerType: string | null;
    ownerName: string | null;
    ownerShipColor: string | null;
    customName: string | null;
    claimRadius: number | null;
  }): void {
    const entry = this.islands.get(islandId);
    if (!entry) {
      console.log(`🏝️ Island ${islandId} not currently loaded, ownership update cached`);
      return;
    }

    // Update the island data
    entry.islandData.ownerId = ownership.ownerId;
    entry.islandData.ownerType = ownership.ownerType as 'player' | 'crew' | 'faction_regime' | 'faction_navy' | 'faction_pirate' | 'faction_merchant' | 'faction_smuggler' | null;
    entry.islandData.ownerName = ownership.ownerName;
    entry.islandData.ownerShipColor = ownership.ownerShipColor;
    entry.islandData.customName = ownership.customName;
    entry.islandData.claimRadius = ownership.claimRadius;

    console.log(`🏝️ Updated island ${islandId} ownership: ${ownership.ownerName || 'unclaimed'}`);

    // Rebuild sprite/labels so visuals update immediately (no refresh required)
    this.rebuildIslandSprite(islandId);
  }
}
